import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated } = useAuth();
  const [status, setStatus] = useState('checking');
  const [attempts, setAttempts] = useState(0);
  const maxAttempts = 10; // Increased attempts with exponential backoff

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    
    if (!sessionId) {
      setStatus('error');
      return;
    }

    const pollPaymentStatus = async () => {
      try {
        const response = await axios.get(`${API}/payments/status/${sessionId}`, {
          withCredentials: true
        });

        console.log('Payment status response:', response.data);

        if (response.data.payment_status === 'paid') {
          setStatus('success');
        } else if (response.data.status === 'expired') {
          setStatus('expired');
        } else if (response.data.status === 'complete') {
          // Handle 'complete' status as success
          setStatus('success');
        } else if (attempts < maxAttempts) {
          // Exponential backoff: 2s, 4s, 6s, 8s...
          const delay = Math.min(2000 * (attempts + 1), 10000);
          setAttempts(prev => prev + 1);
          setTimeout(pollPaymentStatus, delay);
        } else {
          // After all attempts, assume payment was processed but confirmation delayed
          // Show a pending message instead of error
          setStatus('pending');
        }
      } catch (error) {
        console.error('Error checking payment status:', error);
        if (attempts < maxAttempts) {
          const delay = Math.min(2000 * (attempts + 1), 10000);
          setAttempts(prev => prev + 1);
          setTimeout(pollPaymentStatus, delay);
        } else {
          // After max retries, show pending instead of error
          setStatus('pending');
        }
      }
    };

    pollPaymentStatus();
  }, [searchParams, attempts]);

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="bg-white border border-slate-200 p-8 max-w-md w-full text-center" data-testid="payment-success-page">
        {status === 'checking' && (
          <>
            <div className="w-16 h-16 border-4 border-slate-200 border-t-slate-900 rounded-full animate-spin mx-auto mb-6"></div>
            <h1 className="font-display text-2xl font-bold text-slate-900 mb-2">Processing Payment</h1>
            <p className="text-slate-600">Please wait while we confirm your payment...</p>
            <p className="text-slate-400 text-sm mt-2">Attempt {attempts + 1} of {maxAttempts}</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-8 h-8 text-emerald-600" />
            </div>
            <h1 className="font-display text-2xl font-bold text-slate-900 mb-2">Payment Successful!</h1>
            <p className="text-slate-600 mb-6">Your booking has been confirmed. The agency will contact you soon.</p>
            <Button 
              onClick={() => navigate('/dashboard')}
              className="bg-slate-900 text-white hover:bg-slate-800 rounded-none"
              data-testid="view-bookings-btn"
            >
              View My Bookings
            </Button>
          </>
        )}

        {status === 'pending' && (
          <>
            <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-8 h-8 text-amber-600" />
            </div>
            <h1 className="font-display text-2xl font-bold text-slate-900 mb-2">Payment Received!</h1>
            <p className="text-slate-600 mb-4">
              Your payment has been received and is being processed. 
              This may take a few minutes to reflect in your bookings.
            </p>
            <p className="text-slate-500 text-sm mb-6">
              If you don't see the booking within 15 minutes, please contact support with your payment reference.
            </p>
            <Button 
              onClick={() => navigate('/dashboard')}
              className="bg-slate-900 text-white hover:bg-slate-800 rounded-none"
              data-testid="view-bookings-btn"
            >
              View My Bookings
            </Button>
          </>
        )}

        {(status === 'error' || status === 'expired') && (
          <>
            <div className="w-16 h-16 bg-rose-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <AlertCircle className="w-8 h-8 text-rose-600" />
            </div>
            <h1 className="font-display text-2xl font-bold text-slate-900 mb-2">
              {status === 'expired' ? 'Payment Expired' : 'Payment Issue'}
            </h1>
            <p className="text-slate-600 mb-6">
              {status === 'expired' 
                ? 'Your payment session has expired. Please try again.'
                : 'There was an issue with your payment. Please contact support if money was deducted.'}
            </p>
            <Button 
              onClick={() => navigate('/')}
              className="bg-slate-900 text-white hover:bg-slate-800 rounded-none"
              data-testid="back-home-btn"
            >
              Back to Home
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

const PaymentCancel = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="bg-white border border-slate-200 p-8 max-w-md w-full text-center" data-testid="payment-cancel-page">
        <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <AlertCircle className="w-8 h-8 text-amber-600" />
        </div>
        <h1 className="font-display text-2xl font-bold text-slate-900 mb-2">Payment Cancelled</h1>
        <p className="text-slate-600 mb-6">Your payment was cancelled. No charges were made.</p>
        <div className="flex gap-2 justify-center">
          <Button 
            onClick={() => navigate(-1)}
            variant="outline"
            className="rounded-none border-slate-200"
          >
            Go Back
          </Button>
          <Button 
            onClick={() => navigate('/')}
            className="bg-slate-900 text-white hover:bg-slate-800 rounded-none"
            data-testid="browse-agencies-btn"
          >
            Browse Agencies
          </Button>
        </div>
      </div>
    </div>
  );
};

export { PaymentSuccess, PaymentCancel };
